import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Snackbar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";
const fmtAUD = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));

function PatientRow({ patient, batchID, canSplit, onProcessed, onEmailSent, onSplit }) {
  const [processing, setProcessing] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [splitting, setSplitting] = useState(false);

  const scriptIDs = patient.scripts.map(s => s.ScriptID);

  const handleProcess = async () => {
    setProcessing(true);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_PROCESSING_COMCARE_PROCESS, "POST", { scriptIDs });
      onProcessed(patient.CustomerID);
    } catch {
      // silent — parent will show error
    } finally {
      setProcessing(false);
    }
  };

  const handleEmail = async () => {
    setEmailing(true);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_PROCESSING_COMCARE_EMAIL, "POST", {
        scrBatchID: batchID,
        customerID: patient.CustomerID,
        scriptIDs,
      });
      onEmailSent(`Email sent for ${patient.FirstName} ${patient.LastName}`);
    } catch {
      onEmailSent("Failed to send email — check chemist email address.");
    } finally {
      setEmailing(false);
    }
  };

  const handleSplit = async () => {
    setSplitting(true);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_PROCESSING_COMCARE_SPLIT, "POST", {
        scrBatchID: batchID,
        scriptIDs,
      });
      onSplit(patient.CustomerID);
    } catch {
      // silent
    } finally {
      setSplitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 1, borderBottom: "1px solid #f1f5f9" }}>
      <Box sx={{ flex: 1 }}>
        <Typography fontWeight={700} fontSize="0.88rem">
          {patient.FirstName} {patient.LastName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {patient.scripts.length} script{patient.scripts.length !== 1 ? "s" : ""}
        </Typography>
        <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {patient.scripts.map(s => (
            <Chip
              key={s.ScriptID}
              label={`${s.TxtItem} × ${s.Qty} — ${fmtAUD(s.PharmAmount)}`}
              size="small"
              sx={{ fontSize: "0.72rem", background: "#f1f5f9" }}
            />
          ))}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexShrink: 0, mt: 0.25 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={emailing ? <CircularProgress size={12} /> : <EmailOutlinedIcon />}
          disabled={emailing}
          onClick={handleEmail}
          sx={{ fontSize: "0.75rem", borderColor: Z_BLUE, color: Z_BLUE }}
        >
          Email
        </Button>
        {canSplit && (
          <Button
            size="small"
            variant="outlined"
            startIcon={splitting ? <CircularProgress size={12} /> : <CallSplitIcon />}
            disabled={splitting}
            onClick={handleSplit}
            sx={{ fontSize: "0.75rem", borderColor: "#f59e0b", color: "#b45309" }}
          >
            Split
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={processing ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <CheckCircleOutlineIcon />}
          disabled={processing}
          onClick={handleProcess}
          sx={{ fontSize: "0.75rem", background: Z_PURPLE, "&:hover": { background: "#2a1240" } }}
        >
          Process
        </Button>
      </Box>
    </Box>
  );
}

export default function ComcareTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    phappsRequest(API_PATHS.ZEPHYR_PROCESSING_COMCARE, "GET")
      .then(res => setBatches(res?.data ?? []))
      .catch(() => setError("Failed to load ComCare scripts."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const removePatientFromBatch = (batchID, customerID) => {
    setBatches(prev => prev
      .map(b => b.ScrBatchID !== batchID ? b : {
        ...b,
        patients: b.patients.filter(p => p.CustomerID !== customerID),
      })
      .filter(b => b.patients.length > 0)
    );
  };

  const handleSplit = (batchID, customerID) => {
    // After split, the patient's scripts are in a new batch — remove from current view
    removePatientFromBatch(batchID, customerID);
    setToast("Scripts split into a new batch.");
    load();
  };

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
      <CircularProgress sx={{ color: Z_PURPLE }} />
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography fontWeight={700} fontSize="1rem" color={Z_PURPLE}>ComCare & VIC Scripts</Typography>
        {batches.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {batches.length} batch{batches.length !== 1 ? "es" : ""} with paperwork-required scripts
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {batches.length === 0 && (
        <Box sx={{ textAlign: "center", p: 6, color: "text.secondary" }}>
          <Typography>No ComCare or VIC scripts awaiting review.</Typography>
        </Box>
      )}

      {batches.map(batch => (
        <Accordion key={batch.ScrBatchID} defaultExpanded sx={{ mb: 1.5, borderRadius: "8px !important", border: "1px solid #e2e8f0", "&:before": { display: "none" }, boxShadow: "none" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ background: `${Z_PURPLE}06`, borderRadius: "8px 8px 0 0" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              <Typography fontWeight={700} fontSize="0.9rem" color={Z_PURPLE} sx={{ fontFamily: "monospace" }}>
                {batch.ScrBatchCode}
              </Typography>
              <Typography fontSize="0.85rem" color="text.secondary">{batch.ChemistName}</Typography>
              <Chip
                label={`${batch.patients.length} patient${batch.patients.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{ fontSize: "0.72rem", background: `${Z_BLUE}18`, color: Z_BLUE }}
              />
              {batch.canSplit && (
                <Chip label="Can split" size="small" sx={{ fontSize: "0.72rem", background: "#fef3c7", color: "#b45309" }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0.5, pb: 1.5, px: 2 }}>
            {batch.patients.map(patient => (
              <PatientRow
                key={patient.CustomerID || `${patient.FirstName}_${patient.LastName}`}
                patient={patient}
                batchID={batch.ScrBatchID}
                canSplit={batch.canSplit}
                onProcessed={(cid) => removePatientFromBatch(batch.ScrBatchID, cid)}
                onEmailSent={(msg) => setToast(msg)}
                onSplit={(cid) => handleSplit(batch.ScrBatchID, cid)}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
